import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { NgbDatepicker, NgbDateStruct, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject, catchError, debounceTime, distinctUntilChanged, of, switchMap } from 'rxjs';
import { SharedService } from '../../../shared/services/shared.service';
import { AnalyticsService, AnalyticsEvent } from '../../../services/analytics.service';
import { TeeTimeActionPayload, TeeTimeAvailability, TeeTimePickerData, TeeTimePickerSelection, TeeTimePill, TeeTimeRequestAlertFormAction, TeeTimeSubmitBookingFormAction, WaitlistTimeOfDay } from '../../../model/chat.model';
import { environment } from '../../../../environments/environment';

/**
 * Inline course re-query (edit date/players/holes in the results card, tap "Update tee times"
 * → select_course). DISABLED until the backend handles that re-query event. When false the
 * detail-view pickers stay readonly and the Update button is hidden — the original behaviour.
 * Flip to true once the backend support lands to re-enable it.
 */
const TEE_TIME_REQUERY_ENABLED = false;

interface PlaceSuggestion {
  id: string;
  name: string;
  fullName: string;
  center?: [number, number];
}

interface MapboxFeature {
  id: string;
  text?: string;
  place_name?: string;
  center?: [number, number];
}

@Component({
  selector: 'app-book-tee-time',
  imports: [NgbDatepicker, NgbDropdownModule],
  templateUrl: './book-tee-time.component.html',
  styleUrl: './book-tee-time.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookTeeTimeComponent implements OnInit {
  private readonly _router = inject(Router);
  private readonly _sharedService = inject(SharedService);
  private readonly _http = inject(HttpClient);
  private readonly _analytics = inject(AnalyticsService);

  /** When provided (e.g. embedded inside a chat message), populate from this payload */
  readonly data = input<TeeTimePickerData | undefined>(undefined);
  /** When true the component is rendered embedded inside the chat — skip back-button mutation */
  readonly embedded = input<boolean>(false);
  /** When false, all interactive controls (pills, dropdowns, Book button) are disabled — used for stale chat messages */
  readonly interactive = input<boolean>(true);
  /** 'picker' shows recommended/all times; 'search' renders the initial booking form; 'detail' is the per-course view inside a course-list carousel (morning/afternoon shown directly, no waitlist/book button — pill click auto-emits) */
  readonly mode = input<'picker' | 'search' | 'detail'>('picker');
  /** Fires when the user taps a time pill, with a fully formed askQuestion payload */
  readonly timeSelected = output<TeeTimeActionPayload>();

  protected readonly selectedDate = signal<Date>(this.startOfToday());
  protected readonly playerCount = signal(4);
  protected readonly holesCount = signal(18);
  protected readonly priceRange = signal('$34-$66');
  protected readonly courseName = signal('Ritz Carlton Golf Club');
  protected readonly locationId = signal<string>('');
  protected readonly courseLocation = signal<string>('');
  protected readonly countryCode = signal<string>('');
  protected readonly message = signal<string>('');
  protected readonly subtitle = signal<string>('');
  protected readonly place = signal<string>('');
  protected readonly placeCenter = signal<[number, number] | null>(null);
  protected readonly placeQuery = signal<string>('');
  protected readonly placeResults = signal<PlaceSuggestion[]>([]);
  protected readonly placeLoading = signal(false);
  protected readonly placeError = signal<string>('');
  /** Backend-provided validation banner (e.g. "No courses found within 10km") shown on the booking form */
  protected readonly formError = signal<string>('');
  /** Per-course availability flags from the backend (drives Book / Waitlist / Notify button enablement in detail mode) */
  protected readonly availability = signal<TeeTimeAvailability | null>(null);

  protected readonly hasSlots = computed(() => this.availability()?.hasSlots !== false);
  protected readonly noSlotsMessage = computed(() => {
    const fromApi = this.availability()?.noSlotsMessage;
    if (fromApi) return fromApi;
    const name = this.courseName();
    return `Doesn't seem there are any tee times available, try another date. Alternatively we could add you to the waitlist or notify when the tee times available for ${name}.`;
  });
  /** Split the no-slots message into paragraphs at "Alternatively" so it renders as two visually-separated blocks (matches Figma). */
  protected readonly noSlotsParts = computed<string[]>(() => {
    const msg = this.noSlotsMessage();
    const idx = msg.indexOf('Alternatively');
    if (idx > 0) {
      return [msg.slice(0, idx).trim(), msg.slice(idx).trim()];
    }
    return [msg];
  });
  /** Hidden when geolocation is unavailable or the user has denied permission */
  protected readonly canUseMyLocation = signal(true);
  /** True when `place` was filled by the device's geolocation — UI shows "My Location" instead of the resolved address */
  protected readonly placeIsMyLocation = signal(false);

  protected readonly placeDisplay = computed(() =>
    this.placeIsMyLocation() ? 'Nearby my location' : this.place(),
  );

  private readonly placeQuery$ = new Subject<string>();

  /** Tracks whether the user (or backend pre-fill) has actually picked a value. Used to gate Search in search mode. */
  protected readonly dateTouched = signal(false);
  protected readonly playersTouched = signal(false);
  protected readonly holesTouched = signal(false);
  protected readonly placeTouched = signal(false);

  /** Baseline (originally-applied) search values, captured in applyData. Used in detail mode
   *  to detect when the user has changed date/players/holes so we can offer a re-query. */
  private readonly baselineDate = signal<string>('');
  private readonly baselinePlayers = signal<number | null>(null);
  private readonly baselineHoles = signal<number | null>(null);

  protected readonly playerOptions = [1, 2, 3, 4] as const;
  protected readonly holesOptions = [9, 18] as const;

  // Times are always supplied by the backend payload (applyData). No seed/placeholder data —
  // an empty picker renders the no-slots state rather than fake, unbookable pills.
  protected readonly recommendedTimes = signal<readonly TeeTimePill[]>([]);
  protected readonly morningTimes = signal<readonly TeeTimePill[]>([]);
  protected readonly afternoonTimes = signal<readonly TeeTimePill[]>([]);

  /** Stores the apptTime (ISO) of the selected pill, or its label when no apptTime exists (placeholder mode) */
  protected readonly selectedTime = signal<string | null>(null);
  protected readonly selectedPill = signal<TeeTimePill | null>(null);
  protected readonly pickerSelection = signal<TeeTimePickerSelection | null>(null);
  protected readonly showAllTimes = signal(false);

  /** True when the user has selected a pill the BE flagged as unavailable. Drives Book-disable + Alert/Waitlist reveal. */
  protected readonly isSelectedPillUnavailable = computed(() => {
    const pill = this.selectedPill();
    if (pill != null) {
      return pill.available === false;
    }
    // A time can be selected without a captured pill object (e.g. preselected
    // from data via the `selectedTime`-only path). Resolve it against the
    // rendered grids so the unavailable state is still detected.
    const time = this.selectedTime();
    if (!time) {
      return false;
    }
    const allPills = [
      ...this.recommendedTimes(),
      ...this.morningTimes(),
      ...this.afternoonTimes(),
    ];
    const match = allPills.find((p) => (p.apptTime || p.time) === time);
    return match?.available === false;
  });

  /** True when the picker has at least one time to offer across all three grids.
   *  When false in picker mode we render the no-slots / waitlist state instead of an
   *  empty grid or leaked placeholder pills. */
  protected readonly pickerHasTimes = computed(() =>
    this.recommendedTimes().length > 0 ||
    this.morningTimes().length > 0 ||
    this.afternoonTimes().length > 0,
  );

  /** Gate the "Book tee time" CTA on a real, bookable selection — mirrors the
   *  `if (!pill?.apptTime) return;` guard in bookTeeTime() so the button can't be
   *  clickable while doing nothing. */
  protected readonly canBookSelectedPill = computed(() => !!this.selectedPill()?.apptTime);

  protected readonly morningSelected = signal(false);
  protected readonly afternoonSelected = signal(false);

  protected readonly selectedDateLabel = computed(() => {
    const d = this.selectedDate();
    const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
    const day = d.getDate();
    const month = d.toLocaleDateString('en-US', { month: 'long' });
    const year = d.getFullYear();
    return `${weekday}, ${day}${this.ordinalSuffix(day)} ${month} ${year}`;
  });

  protected readonly selectedNgbDate = computed<NgbDateStruct>(() => {
    const d = this.selectedDate();
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
  });

  protected readonly minNgbDate = computed<NgbDateStruct>(() => {
    const d = this.startOfToday();
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
  });

  /** Exposes the re-query feature flag to the template. */
  protected readonly requeryEnabled = TEE_TIME_REQUERY_ENABLED;

  /** In the per-course (detail) view the date/players/holes are frozen (static labels) while
   *  the inline re-query feature is disabled. When TEE_TIME_REQUERY_ENABLED is true they become
   *  editable so the user can re-query this same course (see refreshCourseDetails). */
  protected readonly pickersReadonly = computed(() => !TEE_TIME_REQUERY_ENABLED && this.mode() === 'detail');

  /** True in a single-course view (picker or detail) once the user edits any of
   *  date/players/holes away from the values the card was opened with — drives the
   *  "Update tee times" CTA that re-queries this course. */
  protected readonly courseParamsChanged = computed(() => {
    if (this.mode() !== 'detail' && this.mode() !== 'picker') return false;
    const players = this.baselinePlayers();
    const holes = this.baselineHoles();
    return this.toIsoDate(this.selectedDate()) !== this.baselineDate()
      || (players !== null && this.playerCount() !== players)
      || (holes !== null && this.holesCount() !== holes);
  });

  private startOfToday(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  /** Signature of the last payload we applied. In detail mode the parent binds
   *  `[data]="buildPickerData(...)"` — a method call that returns a NEW object every
   *  change-detection cycle. Without this guard the effect would re-run applyData on
   *  every CD, clobbering the user's in-progress date/players/holes edits (and resetting
   *  the baseline so courseParamsChanged never turns true). buildPickerData is
   *  deterministic, so identical content serialises identically and is skipped; a genuine
   *  refresh has different content and is applied. */
  private lastAppliedSignature: string | null = null;

  constructor() {
    effect(() => {
      const d = this.data();
      if (!d) return;
      let signature: string;
      try {
        signature = JSON.stringify(d);
      } catch {
        signature = '';
      }
      // Same payload content re-emitted (new object reference from a template method
      // call) — don't re-apply, or we'd wipe the user's edits.
      if (signature && signature === this.lastAppliedSignature) return;
      this.lastAppliedSignature = signature;
      this.applyData(d);
    });

    this.placeQuery$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(q => {
        const trimmed = q.trim();
        if (trimmed.length < 2) {
          this.placeLoading.set(false);
          return of<MapboxFeature[]>([]);
        }
        this.placeLoading.set(true);
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(trimmed)}.json`
          + `?access_token=${environment.mapBoxApiKey}&autocomplete=true&limit=5`;
        return this._http.get<{ features: MapboxFeature[] }>(url).pipe(
          switchMap(res => of(res.features ?? [])),
          catchError(() => {
            this.placeError.set('Could not search places. Try again.');
            return of<MapboxFeature[]>([]);
          }),
        );
      }),
    ).subscribe(features => {
      this.placeLoading.set(false);
      this.placeResults.set(features.map(f => ({
        id: f.id,
        name: f.text ?? f.place_name ?? '',
        fullName: f.place_name ?? f.text ?? '',
        center: f.center,
      })));
    });
  }

  ngOnInit(): void {
    if (!this.embedded()) {
      this._sharedService.backButton.set({ show: true, url: '/tee-times', text: '', action: '' });
    }
    // Read the input directly — placeTouched/place signals are populated later by the effect,
    // so geolocation could otherwise race-overwrite a pre-filled selection (e.g. after an
    // empty-list re-prompt with place: "Denver City").
    if (this.mode() === 'search') {
      const d = this.data();
      const hasPrefilledPlace = !!(d?.selection?.place || d?.selection?.coordinates);
      if (!hasPrefilledPlace) {
        this.initGeolocation();
      }
    }
  }

  private initGeolocation(): void {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      this.canUseMyLocation.set(false);
      return;
    }
    const permissions = (navigator as Navigator & { permissions?: Permissions }).permissions;
    if (!permissions?.query) return;
    permissions.query({ name: 'geolocation' as PermissionName })
      .then(result => {
        if (result.state === 'denied') {
          this.canUseMyLocation.set(false);
        } else if (result.state === 'granted') {
          this.useMyLocation(true);
        }
      })
      .catch(() => { /* permissions API unsupported — leave option visible */ });
  }

  private applyData(d: TeeTimePickerData): void {
    if (d.message !== undefined) this.message.set(d.message);
    if (d.subtitle !== undefined) this.subtitle.set(d.subtitle);
    if (d.priceRange) this.priceRange.set(d.priceRange);

    const courseName = d.selection?.courseName ?? d.courseName;
    if (courseName) this.courseName.set(courseName);
    const locationId = d.selection?.locationId ?? d.locationId;
    if (locationId) this.locationId.set(locationId);
    if (d.courseLocation) this.courseLocation.set(d.courseLocation);
    if (d.countryCode) this.countryCode.set(d.countryCode);
    if (d.error) {
      this.formError.set(d.error);
    }

    if (d.availability) {
      this.availability.set(d.availability);
    }

    // Restore place state. Backend may return BOTH a place text AND coordinates
    // (its own geocoding); the typed place wins, coordinates are only treated as
    // "Nearby my location" when there is no place text.
    const coords = d.selection?.coordinates;
    const placeText = d.selection?.place;
    const hasCoords = !!coords && typeof coords.lat === 'number' && typeof coords.lng === 'number';
    if (placeText) {
      this.place.set(placeText);
      this.placeIsMyLocation.set(false);
      if (hasCoords) this.placeCenter.set([coords!.lng, coords!.lat]);
      this.placeTouched.set(true);
    } else if (hasCoords) {
      this.placeCenter.set([coords!.lng, coords!.lat]);
      this.place.set('');
      this.placeIsMyLocation.set(true);
      this.placeTouched.set(true);
    }

    const players = d.selection?.players ?? d.players;
    if (players !== undefined && players !== null) {
      const n = typeof players === 'string' ? parseInt(players, 10) : players;
      if (!Number.isNaN(n) && n > 0) {
        this.playerCount.set(n);
        this.playersTouched.set(true);
      }
    }
    const holes = d.selection?.holes ?? d.holes;
    if (holes !== undefined && holes !== null) {
      const n = typeof holes === 'string' ? parseInt(holes, 10) : holes;
      if (!Number.isNaN(n) && n > 0) {
        this.holesCount.set(n);
        this.holesTouched.set(true);
      }
    }
    const dateStr = d.selection?.date ?? d.date;
    if (dateStr) {
      const parsed = new Date(dateStr);
      if (!Number.isNaN(parsed.getTime())) {
        this.selectedDate.set(parsed);
        this.dateTouched.set(true);
      }
    }

    const tod = d.selection?.timeOfDay;
    if (tod === 'morning') this.morningSelected.set(true);
    else if (tod === 'afternoon') this.afternoonSelected.set(true);

    // This component is backend-driven whenever `data` is applied (chat-embedded picker).
    // Replace the seed placeholder pills with the payload's times unconditionally — even when
    // empty — so a picker that returns no slots renders the no-slots state instead of leaking
    // the hardcoded placeholders (which carry apptTime: '' and make Book silently no-op).
    // The placeholder seeds only survive in the standalone/no-data preview, where applyData never runs.
    this.recommendedTimes.set(d.recommendedTimes ?? []);
    this.morningTimes.set(d.availableTimes?.morning ?? []);
    this.afternoonTimes.set(d.availableTimes?.afternoon ?? []);

    if (d.selection) this.pickerSelection.set(d.selection);

    const preselectedPill = this.resolvePreselectedPill(d);
    if (preselectedPill) {
      this.selectedPill.set(preselectedPill);
      this.selectedTime.set(preselectedPill.apptTime || preselectedPill.time);
    } else if (d.selectedTime) {
      this.selectedTime.set(d.selectedTime);
    }

    // Capture the applied values as the baseline so detail-mode change detection only
    // offers a re-query after the user actually edits date/players/holes. A refreshed
    // card re-runs applyData, which correctly re-establishes the baseline.
    this.baselineDate.set(this.toIsoDate(this.selectedDate()));
    this.baselinePlayers.set(this.playerCount());
    this.baselineHoles.set(this.holesCount());
  }

  protected readonly canSearch = computed(() =>
    this.placeTouched() && this.dateTouched() && this.playersTouched() && this.holesTouched(),
  );

  protected readonly titleText = computed(() => this.message() || `Let’s find you a tee time!`);
  protected readonly subtitleText = computed(() => this.subtitle() || 'Select a date, player and holes');

  private resolvePreselectedPill(d: TeeTimePickerData): TeeTimePill | null {
    const allPills: TeeTimePill[] = [
      ...(d.recommendedTimes ?? []),
      ...(d.availableTimes?.morning ?? []),
      ...(d.availableTimes?.afternoon ?? []),
    ];
    if (d.selectedTime) {
      const byAppt = allPills.find((p) => p.apptTime === d.selectedTime);
      if (byAppt) return byAppt;
    }
    const preferred = d.selection?.preferredTime;
    if (preferred) {
      const target = this.normaliseTime(preferred);
      const byLabel = allPills.find((p) => this.normaliseTime(p.time) === target);
      if (byLabel) return byLabel;
    }
    return null;
  }

  private normaliseTime(value: string): string {
    return (value ?? '').toLowerCase().replace(/\s+/g, '');
  }

  onDatepickerSelect(date: NgbDateStruct): void {
    this.selectedDate.set(new Date(date.year, date.month - 1, date.day));
    this.dateTouched.set(true);
  }

  selectPlayerCount(n: number): void {
    this.playerCount.set(n);
    this.playersTouched.set(true);
  }

  selectHolesCount(n: number): void {
    this.holesCount.set(n);
    this.holesTouched.set(true);
  }

  selectTime(pill: TeeTimePill): void {
    this.selectedTime.set(pill.apptTime || pill.time);
    this.selectedPill.set(pill);
  }

  toggleAllTimes(): void {
    this.showAllTimes.update(v => !v);
  }

  bookTeeTime(): void {
    const pill = this.selectedPill();
    if (!pill?.apptTime) return;

    const selection = this.pickerSelection();
    const dateIso = pill.apptTime.slice(0, 10);
    const base = selection
      ? {
          courseName: selection.courseName ?? this.courseName(),
          locationId: selection.locationId ?? this.locationId(),
          date: selection.date || dateIso,
          players: selection.players ?? this.playerCount(),
          holes: selection.holes ?? this.holesCount(),
        }
      : {
          courseName: this.courseName(),
          locationId: this.locationId(),
          date: dateIso,
          players: this.playerCount(),
          holes: this.holesCount(),
        };

    // Forward the original search context (place / coordinates) so the backend
    // can keep grounding the booking flow against the same area as submit_booking_form.
    const placeFromSelection = selection?.place;
    const coordsFromSelection = selection?.coordinates;
    const localCenter = this.placeCenter();
    const place = placeFromSelection ?? (this.placeIsMyLocation() ? undefined : this.place() || undefined);
    const coordinates = coordsFromSelection ?? (localCenter ? { lat: localCenter[1], lng: localCenter[0] } : undefined);

    this._analytics.track(AnalyticsEvent.TEE_TIME_SELECTED, {
      appointment_time: pill.apptTime,
      course_name: base.courseName,
      players: base.players,
      holes: base.holes,
    });

    this.timeSelected.emit({
      question: `Select ${pill.time}`,
      action: {
        type: 'select_time',
        ...base,
        appointmentTime: pill.apptTime,
        pricePerPatron: pill.pricePerPatron,
        // Thread the course capability so booking-review can gate the prepay option.
        ...(selection?.supportedRequestTypes ? { supportedRequestTypes: selection.supportedRequestTypes } : {}),
        ...(place ? { place } : {}),
        ...(coordinates ? { coordinates } : {}),
        ...(selection?.previousBookingId ? { previousBookingId: selection.previousBookingId } : {}),
      },
    });
  }

  requestWaitlistForm(): void {
    const selection = this.pickerSelection();
    const dateIso = selection?.date || this.toIsoDate(this.selectedDate());
    const players = selection?.players ?? this.playerCount();
    const holes = selection?.holes ?? this.holesCount();
    const courseName = selection?.courseName ?? this.courseName();
    const locationId = selection?.locationId ?? this.locationId();

    const sel = selection;
    const timeOfDay: WaitlistTimeOfDay =
      sel?.timeOfDay === 'morning' || sel?.timeOfDay === 'afternoon' ? sel.timeOfDay : 'any';

    const placeFromSelection = sel?.place;
    const coordsFromSelection = sel?.coordinates;
    const localCenter = this.placeCenter();
    const place = placeFromSelection ?? (this.placeIsMyLocation() ? undefined : this.place() || undefined);
    const coordinates = coordsFromSelection ?? (localCenter ? { lat: localCenter[1], lng: localCenter[0] } : undefined);

    this.timeSelected.emit({
      question: `Add me to the waitlist for ${courseName}`,
      action: {
        type: 'request_waitlist_form',
        courseName,
        locationId,
        ...(this.courseLocation() ? { courseLocation: this.courseLocation() } : {}),
        ...(this.countryCode() ? { countryCode: this.countryCode() } : {}),
        date: dateIso,
        timeOfDay,
        players,
        holes,
        ...(place ? { place } : {}),
        ...(coordinates ? { coordinates } : {}),
      },
    });
  }

  requestAlertForm(): void {
    const selection = this.pickerSelection();
    const dateIso = selection?.date || this.toIsoDate(this.selectedDate());
    const players = selection?.players ?? this.playerCount();
    const holes = selection?.holes ?? this.holesCount();
    const courseName = selection?.courseName ?? this.courseName();
    const locationId = selection?.locationId ?? this.locationId();
    // Prefer the pill the user just clicked — if they tapped a greyed 1:30pm pill, alert for 1:30pm
    // even when the original search appointmentTime was 2pm. Fall back to the BE-echoed selection
    // for the card-level "Alert me" CTA where no pill is selected.
    const pill = this.selectedPill();
    const appointmentTime = pill?.apptTime || selection?.appointmentTime;
    if (!appointmentTime) {
      console.warn('[alert] cannot dispatch request_alert_form — no appointmentTime in selection or selected pill');
      return;
    }

    const courseLocation = selection?.courseLocation ?? this.courseLocation();
    const countryCode = selection?.countryCode ?? this.countryCode();
    const placeFromSelection = selection?.place;
    const coordsFromSelection = selection?.coordinates;
    const localCenter = this.placeCenter();
    const place = placeFromSelection ?? (this.placeIsMyLocation() ? undefined : this.place() || undefined);
    const coordinates = coordsFromSelection ?? (localCenter ? { lat: localCenter[1], lng: localCenter[0] } : undefined);
    const pricePerPatron = selection?.pricePerPatron ?? pill?.pricePerPatron;

    const action: TeeTimeRequestAlertFormAction = {
      type: 'request_alert_form',
      courseName,
      locationId,
      ...(courseLocation ? { courseLocation } : {}),
      ...(countryCode ? { countryCode } : {}),
      date: dateIso,
      appointmentTime,
      players,
      holes,
      ...(pricePerPatron != null ? { pricePerPatron } : {}),
      ...(place ? { place } : {}),
      ...(coordinates ? { coordinates } : {}),
    };

    const displayTime = selection?.preferredTime ?? pill?.time ?? 'my slot';
    this.timeSelected.emit({
      question: `Alert me when ${displayTime} at ${courseName} opens`,
      action,
    });
  }

  toggleMorning(): void {
    this.morningSelected.update(v => !v);
  }

  toggleAfternoon(): void {
    this.afternoonSelected.update(v => !v);
  }

  /** Search field inside the place dropdown — focused when the dropdown opens */
  private readonly placeInput = viewChild<ElementRef<HTMLInputElement>>('placeInput');

  /** Focus the search input as soon as the place dropdown opens */
  onPlaceDropdownToggle(open: boolean): void {
    if (!open) return;
    setTimeout(() => this.placeInput()?.nativeElement.focus(), 0);
  }

  onPlaceInput(value: string): void {
    this.placeQuery.set(value);
    this.placeError.set('');
    this.placeQuery$.next(value);
  }

  selectPlaceFromResults(s: PlaceSuggestion): void {
    this.place.set(s.fullName || s.name);
    this.placeCenter.set(s.center ?? null);
    this.placeIsMyLocation.set(false);
    this.placeTouched.set(true);
    this.placeQuery.set('');
    this.placeResults.set([]);
  }

  clearPlace(): void {
    this.place.set('');
    this.placeCenter.set(null);
    this.placeIsMyLocation.set(false);
    this.placeTouched.set(false);
    this.placeQuery.set('');
    this.placeResults.set([]);
    this.placeError.set('');
  }

  useMyLocation(silent = false): void {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      this.canUseMyLocation.set(false);
      return;
    }
    this.placeLoading.set(true);
    this.placeError.set('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json`
          + `?access_token=${environment.mapBoxApiKey}&limit=1`;
        this._http.get<{ features: MapboxFeature[] }>(url).pipe(
          catchError(() => of({ features: [] as MapboxFeature[] })),
        ).subscribe(res => {
          this.placeLoading.set(false);
          const f = res.features?.[0];
          const resolved = f?.place_name || f?.text || '';
          this.place.set(resolved);
          this.placeCenter.set([longitude, latitude]);
          this.placeIsMyLocation.set(true);
          this.placeTouched.set(true);
        });
      },
      (err) => {
        this.placeLoading.set(false);
        // Hide the option once the user denies — they can still search by name.
        // Other failures (timeout, position unavailable) also hide it; the user will use search.
        this.canUseMyLocation.set(false);
      },
      { timeout: 8000, maximumAge: 60000 },
    );
  }

  searchTeeTimes(): void {
    if (!this.canSearch()) return;
    const dateIso = this.toIsoDate(this.selectedDate());
    const players = this.playerCount();
    const holes = this.holesCount();
    const morning = this.morningSelected();
    const afternoon = this.afternoonSelected();
    const timeOfDay: 'morning' | 'afternoon' | '' =
      morning && !afternoon ? 'morning'
      : afternoon && !morning ? 'afternoon'
      : '';

    // Always include coordinates when we have them — backend prefers coords ($nearSphere)
    // and Mapbox autocomplete already populates `placeCenter` for typed places.
    // For "Nearby my location", we suppress the reverse-geocoded place text and send geo only.
    const usingGeo = this.placeIsMyLocation();
    const center = this.placeCenter();
    const action: TeeTimeSubmitBookingFormAction = {
      type: 'submit_booking_form',
      date: dateIso,
      players,
      holes,
      timeOfDay,
      radiusKm: 100,
    };
    if (center) {
      action.coordinates = { lat: center[1], lng: center[0] };
    }
    if (!usingGeo && this.place()) {
      action.place = this.place();
    }

    this._analytics.track(AnalyticsEvent.TEE_TIME_SEARCH, {
      date: dateIso,
      players,
      holes,
      time_of_day: timeOfDay,
    });

    this.timeSelected.emit({ question: 'Search tee times', action });
  }

  /** Detail (per-course) view: re-query THIS course for the currently-selected
   *  date/players/holes. The backend replies with a refreshed course card, so the
   *  updated times render as a new chat response (same pattern as selecting a time). */
  refreshCourseDetails(): void {
    if (this.mode() !== 'detail' && this.mode() !== 'picker') return;
    const selection = this.pickerSelection();
    const courseName = selection?.courseName ?? this.courseName();
    const locationId = selection?.locationId ?? this.locationId();
    if (!locationId) return;
    const dateIso = this.toIsoDate(this.selectedDate());
    const players = this.playerCount();
    const holes = this.holesCount();
    const preferredTime = selection?.preferredTime ?? '';

    this._analytics.track(AnalyticsEvent.TEE_TIME_SEARCH, {
      date: dateIso,
      players,
      holes,
      time_of_day: '',
    });

    this.timeSelected.emit({
      question: `Show tee times at ${courseName} on ${dateIso}`,
      action: {
        type: 'select_course',
        courseName,
        locationId,
        date: dateIso,
        players,
        holes,
        preferredTime,
      },
    });
  }

  private toIsoDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private ordinalSuffix(n: number): string {
    const tens = n % 100;
    if (tens >= 11 && tens <= 13) return 'th';
    switch (n % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }
}
